import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Home, Users, Briefcase, MessageCircle, Bell, User, Menu, X, LogOut, Settings, HelpCircle, Award } from 'lucide-react';
import { Input } from '../ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatform } from '../../contexts/PlatformContext';
import PlatformToggle from './PlatformToggle';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const { user, logout } = useAuth();
  const { currentMode, toggleMode, businessNotifications } = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigationItems = [
    { icon: Home, label: 'Home', path: '/', count: null },
    { icon: Users, label: 'My Network', path: '/network', count: 12 },
    { icon: Briefcase, label: 'Jobs', path: '/jobs', count: null },
    { icon: Briefcase, label: 'Opportunities', path: '/opportunities', count: null },
    { icon: MessageCircle, label: 'Messages', path: '/messages', count: 3 },
    { icon: Bell, label: 'Notifications', path: '/notifications', count: 7 },
    { icon: User, label: 'Me', path: '/profile', count: null }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Desktop Header */}
      <header className="bg-white shadow-soft border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo and Search */}
            <div className="flex items-center space-x-4 flex-1">
              <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded flex items-center justify-center shadow-soft">
                  <span className="text-white font-bold text-sm">TL</span>
                </div>
                <span className="font-semibold text-blue-600 hidden sm:block tracking-tight">TrainerLinked</span>
              </Link>
              
              <div className={`relative max-w-md flex-1 transition-all duration-300 ${searchFocused ? 'scale-105' : 'scale-100'}`}>
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${searchFocused ? 'text-blue-500' : 'text-gray-400'}`} />
                <Input
                  type="text"
                  placeholder="Search trainers, companies, jobs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  className={`pl-10 bg-gray-50 border-none focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all duration-200 search-focus ${searchFocused ? 'shadow-soft' : ''}`}
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-100"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Platform Toggle - Center */}
            <div className="flex justify-center flex-1">
              <PlatformToggle 
                currentMode={currentMode}
                onToggle={toggleMode}
                businessNotifications={businessNotifications}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex flex-col items-center px-3 py-2 rounded-lg transition-all duration-200 nav-item relative group ${
                      isActive(item.path)
                        ? 'text-blue-600 bg-blue-50 active'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="relative">
                      <Icon className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                      {item.count && (
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-2 -right-2 px-1.5 py-0 text-xs min-w-[18px] h-[18px] flex items-center justify-center notification-badge shadow-soft"
                        >
                          {item.count}
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs mt-1 font-medium transition-all duration-200 group-hover:font-semibold">{item.label}</span>
                    
                    {/* Active indicator */}
                    {isActive(item.path) && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-blue-600 rounded-full"></div>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* User Avatar & Mobile Menu */}
            <div className="flex items-center space-x-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="focus:outline-none">
                    <Avatar className="w-8 h-8 avatar-hover ring-2 ring-transparent hover:ring-blue-200 transition-all duration-300 cursor-pointer">
                      <AvatarImage src={user?.avatar} alt={user?.name} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold">
                        {user?.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      View Profile
                    </Link>
                  </DropdownMenuItem>
                  {user?.user_type === 'trainer' && (
                    <DropdownMenuItem asChild>
                      <Link to="/digitpro" className="flex items-center cursor-pointer">
                        <Award className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="text-blue-600 font-medium">View my DigiTPro</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings & Privacy
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/help" className="flex items-center cursor-pointer">
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Help Center
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden hover:bg-gray-100 transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-x-0 top-14 z-50 bg-white border-b shadow-strong animate-in slide-in-from-top-2 duration-200">
          <nav className="max-w-7xl mx-auto px-4 py-4">
            <div className="grid grid-cols-2 gap-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive(item.path)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="relative">
                      <Icon className="w-5 h-5" />
                      {item.count && (
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-1 -right-1 px-1 py-0 text-xs min-w-[16px] h-[16px] flex items-center justify-center"
                        >
                          {item.count > 9 ? '9+' : item.count}
                        </Badge>
                      )}
                    </div>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 top-14 bg-black/20 z-40 animate-in fade-in duration-200"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;